import _ from 'lodash'
import { ID, Model } from '../types'
import { getNextItemIndex, getNextItem } from '../utils/array'

export interface UserGameHandState {
  userId: ID
  state: Model.UserHandState
  tableState: Model.UserTableState
  votesToSetUserToAway: ID[]
  amountLeftToBet: number
  amountInvestedInPreviousRounds: number
  amountInvestedInCurrentRound: number
  amountInvested: number
  pendingAmountGiven: number
  isNextToAct: boolean
  isDealer: boolean
  isCurrentUser: boolean
  lastActionThisRound?: Model.GameHandAction
}

export const buildUserGameHandStates = (gameHand: Model.GameHand, currentUserId: string) => {
  const firstToActIndex = getNextItemIndex(
    gameHand.userTablePosition,
    gameHand.userTablePosition.findIndex((userId) => userId === gameHand.dealerUserId)!,
  )
  const userActionOrder = [
    ...gameHand.userTablePosition.slice(firstToActIndex),
    ...gameHand.userTablePosition.slice(0, firstToActIndex),
  ]
  const userGameHandStates = gameHand.userTablePosition.reduce((accum, userId, index) => {
    accum[userId] = {
      userId,
      isCurrentUser: userId === currentUserId,
      tableState: gameHand.userTableStates[userId],
      pendingAmountGiven: gameHand.pendingActionsForNextHand?.givenChips
        ? gameHand.pendingActionsForNextHand?.givenChips[userId] || 0
        : 0,
      state: gameHand.userTableStates[userId] === 'OUT' ? 'OUT' : 'IN',
      amountLeftToBet: gameHand.chipsAtStartOfHand[userId],
      amountInvested: 0,
      amountInvestedInCurrentRound: 0,
      amountInvestedInPreviousRounds: 0,
      votesToSetUserToAway: gameHand.votesToSetUserToAway
        ? gameHand.votesToSetUserToAway[userId] || []
        : [],
      isNextToAct: gameHand.nextUserIdToAct === userId,
      isDealer: userId === gameHand.dealerUserId,
    }
    return accum
  }, {} as Record<ID, UserGameHandState>)
  const userIdsInHand = userActionOrder.filter(
    (userId) => userGameHandStates[userId].state !== 'OUT',
  )
  const currentRoundIndex = gameHand.bettingRounds.length - 1
  // add big/small blinds
  const blindsRoundKey =
    currentRoundIndex > 0 ? 'amountInvestedInPreviousRounds' : 'amountInvestedInCurrentRound'
  const smallBlindUserId = userIdsInHand[0]
  const bigBlindUserId = getNextItem(userIdsInHand, 0)
  userGameHandStates[smallBlindUserId][blindsRoundKey] += gameHand.rules.blindAmount / 2
  userGameHandStates[bigBlindUserId][blindsRoundKey] += gameHand.rules.blindAmount

  // go over all the actions that have occurred thus far this hand
  gameHand.bettingRounds.forEach((round, roundIndex) => {
    round.actions.forEach((action) => {
      const isCurrentRound = currentRoundIndex === roundIndex
      if (isCurrentRound) {
        userGameHandStates[action.userId].lastActionThisRound = action
      }
      if (action.type === 'FOLD') {
        userGameHandStates[action.userId].state = 'FOLDED'
      } else if (action.type === 'BET') {
        if (isCurrentRound) {
          userGameHandStates[action.userId].amountInvestedInCurrentRound += action.chipsAmount
        } else {
          userGameHandStates[action.userId].amountInvestedInPreviousRounds += action.chipsAmount
        }
      }
    })
  })
  userActionOrder
    .map((userId) => userGameHandStates[userId])
    .forEach((state) => {
      state.amountInvested =
        state.amountInvestedInCurrentRound + state.amountInvestedInPreviousRounds
      state.amountLeftToBet = state.amountLeftToBet - state.amountInvested
      if (state.amountLeftToBet === 0 && state.state !== 'OUT') {
        state.state = 'ALL_IN'
      }
    })
  return userGameHandStates
}
